<div class="employee-management-container">
  <mat-card>
    <mat-card-header>
      <mat-card-title>
        <mat-icon>people</mat-icon>
        Employee Management
      </mat-card-title>
      <mat-card-subtitle>Manage employees and their fingerprint IDs</mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
      <!-- Add Employee Button -->
      <div class="actions-row">
        <button mat-raised-button color="primary" (click)="showAddForm()">
          <mat-icon>add</mat-icon>
          Add Employee
        </button>
      </div>

      <!-- Search Filter -->
      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Search Employees</mat-label>
        <input matInput (keyup)="applyFilter($event)" placeholder="Search by name, ID, or fingerprint ID" #input>
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>

      <!-- Employee Form -->
      <div *ngIf="isFormVisible" class="form-container">
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ isEditMode ? 'Edit' : 'Add' }} Employee</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <form [formGroup]="employeeForm" (ngSubmit)="onSubmit()">
              <div class="form-row">
                <mat-form-field appearance="outline">
                  <mat-label>First Name</mat-label>
                  <input matInput formControlName="firstName" placeholder="Enter first name">
                  <mat-error *ngIf="employeeForm.get('firstName')?.hasError('required')">
                    First name is required
                  </mat-error>
                  <mat-error *ngIf="employeeForm.get('firstName')?.hasError('minlength')">
                    First name must be at least 2 characters
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Last Name</mat-label>
                  <input matInput formControlName="lastName" placeholder="Enter last name">
                  <mat-error *ngIf="employeeForm.get('lastName')?.hasError('required')">
                    Last name is required
                  </mat-error>
                  <mat-error *ngIf="employeeForm.get('lastName')?.hasError('minlength')">
                    Last name must be at least 2 characters
                  </mat-error>
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-form-field appearance="outline">
                  <mat-label>Person ID</mat-label>
                  <input matInput formControlName="personId" placeholder="Enter person ID">
                  <mat-error *ngIf="employeeForm.get('personId')?.hasError('required')">
                    Person ID is required
                  </mat-error>
                  <mat-error *ngIf="employeeForm.get('personId')?.hasError('minlength')">
                    Person ID must be at least 3 characters
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Fingerprint ID</mat-label>
                  <input matInput formControlName="fingerprintId" placeholder="Enter fingerprint ID (optional)">
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-form-field appearance="outline">
                  <mat-label>Salary Type</mat-label>
                  <mat-select formControlName="salaryType">
                    <mat-option *ngFor="let type of salaryTypes" [value]="type.value">
                      {{ type.label }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline" *ngIf="employeeForm.get('salaryType')?.value === 1">
                  <mat-label>Hourly Rate ($)</mat-label>
                  <input matInput type="number" formControlName="hourlyRate" placeholder="0.00">
                  <mat-error *ngIf="employeeForm.get('hourlyRate')?.hasError('required')">
                    Hourly rate is required
                  </mat-error>
                  <mat-error *ngIf="employeeForm.get('hourlyRate')?.hasError('min')">
                    Hourly rate must be positive
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" *ngIf="employeeForm.get('salaryType')?.value === 2">
                  <mat-label>Monthly Salary ($)</mat-label>
                  <input matInput type="number" formControlName="monthlySalary" placeholder="0.00">
                  <mat-error *ngIf="employeeForm.get('monthlySalary')?.hasError('required')">
                    Monthly salary is required
                  </mat-error>
                  <mat-error *ngIf="employeeForm.get('monthlySalary')?.hasError('min')">
                    Monthly salary must be positive
                  </mat-error>
                </mat-form-field>
              </div>

              <div class="form-row" *ngIf="isEditMode">
                <mat-checkbox formControlName="isActive">
                  Active Employee
                </mat-checkbox>
              </div>

              <!-- Photo Upload Section -->
              <div class="form-row">
                <div class="photo-upload-section">
                  <h4>Employee Photo</h4>
                  <div class="photo-container">
                    <!-- Photo Preview -->
                    <div class="photo-preview" *ngIf="photoPreview">
                      <img [src]="photoPreview" alt="Employee Photo" class="employee-photo">
                      <button mat-icon-button color="warn" (click)="removePhoto()" class="remove-photo-btn">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                    
                    <!-- Upload Button -->
                    <div class="upload-section" *ngIf="!photoPreview">
                      <input type="file" 
                             #photoInput 
                             accept="image/*" 
                             (change)="onPhotoSelected($event)" 
                             style="display: none;">
                      <button mat-stroked-button 
                              type="button" 
                              (click)="photoInput.click()"
                              class="upload-btn">
                        <mat-icon>add_a_photo</mat-icon>
                        Upload Photo
                      </button>
                      <p class="upload-hint">Max size: 5MB. Supported formats: JPG, PNG, GIF</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-actions">
                <button mat-button type="button" (click)="hideForm()">Cancel</button>
                <button mat-raised-button color="primary" type="submit" [disabled]="employeeForm.invalid">
                  {{ isEditMode ? 'Update' : 'Create' }} Employee
                </button>
              </div>
            </form>
          </mat-card-content>
        </mat-card>
      </div>

      <!-- Employee Table -->
      <div class="table-container mat-elevation-z8">
        <table mat-table [dataSource]="dataSource" matSort>
          <!-- ID Column -->
          <ng-container matColumnDef="id">
            <th mat-header-cell *matHeaderCellDef mat-sort-header> ID </th>
            <td mat-cell *matCellDef="let employee"> {{employee.id}} </td>
          </ng-container>

          <!-- Photo Column -->
          <ng-container matColumnDef="photo">
            <th mat-header-cell *matHeaderCellDef> Photo </th>
            <td mat-cell *matCellDef="let employee">
              <div class="employee-photo-cell">
                <img *ngIf="employee.photo" 
                     [src]="employee.photo" 
                     alt="Employee Photo" 
                     class="employee-table-photo">
                <div *ngIf="!employee.photo" class="no-photo">
                  <mat-icon>person</mat-icon>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Full Name Column -->
          <ng-container matColumnDef="fullName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header> Name </th>
            <td mat-cell *matCellDef="let employee"> {{employee.fullName}} </td>
          </ng-container>

          <!-- Person ID Column -->
          <ng-container matColumnDef="personId">
            <th mat-header-cell *matHeaderCellDef mat-sort-header> Person ID </th>
            <td mat-cell *matCellDef="let employee"> {{employee.personId}} </td>
          </ng-container>

          <!-- Fingerprint ID Column -->
          <ng-container matColumnDef="fingerprintId">
            <th mat-header-cell *matHeaderCellDef mat-sort-header> Fingerprint ID </th>
            <td mat-cell *matCellDef="let employee"> {{employee.fingerprintId || 'Not set'}} </td>
          </ng-container>

          <!-- Salary Type Column -->
          <ng-container matColumnDef="salaryType">
            <th mat-header-cell *matHeaderCellDef mat-sort-header> Salary Type </th>
            <td mat-cell *matCellDef="let employee"> {{getSalaryTypeLabel(employee.salaryType)}} </td>
          </ng-container>

          <!-- Hourly Rate Column -->
          <ng-container matColumnDef="hourlyRate">
            <th mat-header-cell *matHeaderCellDef mat-sort-header> Hourly Rate </th>
            <td mat-cell *matCellDef="let employee"> 
              <span *ngIf="employee.salaryType === 1">${{employee.hourlyRate}}/hr</span>
              <span *ngIf="employee.salaryType === 2">-</span>
            </td>
          </ng-container>

          <!-- Monthly Salary Column -->
          <ng-container matColumnDef="monthlySalary">
            <th mat-header-cell *matHeaderCellDef mat-sort-header> Monthly Salary </th>
            <td mat-cell *matCellDef="let employee"> 
              <span *ngIf="employee.salaryType === 2">${{employee.monthlySalary}}/month</span>
              <span *ngIf="employee.salaryType === 1">-</span>
            </td>
          </ng-container>

          <!-- Active Status Column -->
          <ng-container matColumnDef="isActive">
            <th mat-header-cell *matHeaderCellDef mat-sort-header> Status </th>
            <td mat-cell *matCellDef="let employee"> 
              <mat-chip-list>
                <mat-chip [color]="employee.isActive ? 'accent' : 'warn'" selected>
                  {{employee.isActive ? 'Active' : 'Inactive'}}
                </mat-chip>
              </mat-chip-list>
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef> Actions </th>
            <td mat-cell *matCellDef="let employee">
              <button mat-icon-button color="primary" (click)="showEditForm(employee)" matTooltip="Edit Employee">
                <mat-icon>edit</mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
        </table>

        <mat-paginator [pageSizeOptions]="[5, 10, 25, 100]" aria-label="Select page of employees"></mat-paginator>

        <!-- Loading Spinner -->
        <div *ngIf="loading" class="loading-container">
          <mat-spinner></mat-spinner>
          <p>Loading employees...</p>
        </div>

        <!-- No Data Message -->
        <div *ngIf="!loading && dataSource.data.length === 0" class="no-data-container">
          <mat-icon>people_outline</mat-icon>
          <p>No employees found</p>
        </div>
      </div>
    </mat-card-content>
  </mat-card>
</div> 